import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar, FileText, Clock, ArrowLeft } from "lucide-react";
import { Button } from "../../components/ui/button";
import { useSelector } from "react-redux";
import { createSelector } from "reselect";
import AttendancePanel from "../../components/Attendance/AttendancePanel";
import TodayAttendanceStatus from "../../components/Attendance/TodayAttendanceStatus";
import { useGetUserByEmailQuery } from "../../slices/userApiSlice";
import { useGetCalendarByAcademyYearQuery } from "../../slices/calendarApiSlice";
// import "./css/mobile-responsive.css";

const selectUserInfo = (state) => state.auth.userInfo || {};
const getUserEmail = createSelector(
  selectUserInfo,
  (userInfo) => userInfo?.username || "",
);

export default function TechnicianAttendance() {
  const navigate = useNavigate();
  const email = useSelector(getUserEmail);
  const { data: userByEmail } = useGetUserByEmailQuery(email, {
    skip: !email,
  });

  // Get academy ID from user data
  const academyId = userByEmail?.user?.academyId;
  const currentYear = new Date().getFullYear();

  // Check if calendar exists for current year
  const {
    data: calendarData,
    isLoading: isLoadingCalendar,
    error: calendarError,
  } = useGetCalendarByAcademyYearQuery(
    {
      academy_id: academyId,
      year: currentYear,
    },
    {
      skip: !academyId,
    },
  );

  // Check if calendar exists
  const hasCalendar = useMemo(() => {
    if (!academyId) return false;
    if (isLoadingCalendar) return true; // Assume exists while loading to avoid flicker
    if (calendarError) {
      const status = calendarError?.status || calendarError?.originalStatus;
      if (status === 404 || status === "FETCH_ERROR") {
        return false;
      }
      return true;
    }
    return (
      calendarData?.calendar !== null && calendarData?.calendar !== undefined
    );
  }, [calendarData, calendarError, isLoadingCalendar, academyId]);

  const handleClockIn = () => {
    // TODO: Implement clock in functionality with camera and location capture
    console.log("Clock in clicked");
  };

  const handleNavigateToRecords = (path) => {
    navigate(path);
  };

  // If no calendar exists, show message
  if (!hasCalendar && !isLoadingCalendar) {
    return (
      <div className="flex items-center justify-center h-full bg-white">
        <div className="text-center p-8">
          <Calendar className="w-16 h-16 text-zinc-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-zinc-800 mb-2">
            No Calendar Created
          </h2>
          <p className="text-zinc-600">
            Please come back when a calendar has been created for your academy.
          </p>
        </div>
      </div>
    );
  }
  return (
    <div className="flex flex-col h-full bg-zinc-50/50">
      {/* Header Area */}
      <div className="bg-white border-b px-6 py-6 sm:px-8">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => window.history.back()}
            className="h-9 w-9 rounded-full sm:hidden"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="space-y-1">
            <h1 className="text-2xl sm:text-3xl font-black text-zinc-900 tracking-tight">
              Attendance
            </h1>
            <p className="text-sm font-medium text-zinc-500">
              Manage your daily clock-ins and leave requests
            </p>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="grid grid-cols-1 xl:grid-cols-2">
          {/* Main Attendance Panel */}
          <div className="border-b xl:border-b-0 xl:border-r">
            <AttendancePanel />
          </div>

          {/* Other Options Section */}
          <div className="bg-white py-8 px-6 sm:px-8 space-y-8">
            <div className="space-y-1">
              <h2 className="text-xl font-bold text-zinc-900">Quick Actions</h2>
              <p className="text-sm text-zinc-500">
                Shortcut links for common attendance tasks
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 2xl:grid-cols-2 gap-4">
              {/* My Leave Records Card */}
              <button
                onClick={() =>
                  handleNavigateToRecords(
                    "/technician/attendance/leave-records",
                  )
                }
                className="group relative flex items-start gap-4 p-4 rounded-xl border border-zinc-200 bg-white hover:border-zinc-900 hover:shadow-md transition-all text-left"
              >
                <div className="rounded-lg bg-indigo-50 p-2.5 text-indigo-600 group-hover:bg-indigo-600 group-hover:text-white transition-colors">
                  <Calendar className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-zinc-900 text-sm">
                    Leave History
                  </h3>
                  <p className="text-xs text-zinc-500 line-clamp-2 mt-0.5">
                    Check leave history and current balance
                  </p>
                </div>
              </button>

              {/* My Attendance Records Card */}
              <button
                onClick={() =>
                  handleNavigateToRecords("/technician/attendance/record")
                }
                className="group relative flex items-start gap-4 p-4 rounded-xl border border-zinc-200 bg-white hover:border-zinc-900 hover:shadow-md transition-all text-left"
              >
                <div className="rounded-lg bg-emerald-50 p-2.5 text-emerald-600 group-hover:bg-emerald-600 group-hover:text-white transition-colors">
                  <FileText className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-zinc-900 text-sm">
                    Attendance Log
                  </h3>
                  <p className="text-xs text-zinc-500 line-clamp-2 mt-0.5">
                    Review your work hours and daily logs
                  </p>
                </div>
              </button>

              {/* Apply Leave Card */}
              <button
                onClick={() =>
                  handleNavigateToRecords("/technician/attendance/apply-leave")
                }
                className="group relative flex items-start gap-4 p-4 rounded-xl border border-zinc-200 bg-white hover:border-zinc-900 hover:shadow-md transition-all text-left sm:col-span-2 lg:col-span-1 2xl:col-span-2"
              >
                <div className="rounded-lg bg-rose-50 p-2.5 text-rose-600 group-hover:bg-rose-600 group-hover:text-white transition-colors">
                  <Clock className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-zinc-900 text-sm">
                    Request Leave
                  </h3>
                  <p className="text-xs text-zinc-500 line-clamp-2 mt-0.5">
                    Submit a new leave application for approval
                  </p>
                </div>
              </button>
            </div>

            {/* Today's Attendance Status */}
            <div className="pt-8 border-t">
              <TodayAttendanceStatus />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
